// Shared HTML composition helpers for the chat surface preview iframe.
// Extracted from EmailChatSurface so sibling-variant preview frames can use
// the same pipeline — without it, an active variant whose body is body-only
// (older format) gets default header/footer spliced in, while a sibling
// rendering raw sourceHtml would not, so the two frames look different even
// when the underlying templates row is identical.

export function extractInnerBody(html: string): string {
  const match = html.match(/<body\b[^>]*>([\s\S]*?)<\/body>/i);
  return match ? match[1] : html;
}

export function bodyHtmlContainsHeaderOrFooter(html: string): boolean {
  if (!html) return false;
  return /<section\b[^>]*\bdata-section-name\s*=\s*["'](?:header|footer)["']/i.test(
    html,
  );
}

// Per-side guards so a body that owns ONLY a header section doesn't suppress
// the default footer splice (and vice versa). Older bodyHtmlContainsHeaderOrFooter
// returned true on either, which dropped both splices when only one was
// authored — losing the client's default footer.
export function bodyHtmlContainsHeader(html: string): boolean {
  if (!html) return false;
  return /<section\b[^>]*\bdata-section-name\s*=\s*["']header["']/i.test(html);
}

// True when the body is converted (canvas-origin) source: it carries a
// `data-render-mode="image"` canvas section (header baked into the rendered
// block) plus its own footer, so the chat must NOT splice its default
// header/footer on top — doing so double-stacks the logo/footer.
export function bodyHtmlIsCanvasOrigin(html: string): boolean {
  if (!html) return false;
  return (
    /data-render-mode\s*=\s*["']image["']/i.test(html) ||
    /\bdata-nm-doc\b/i.test(html)
  );
}

export function bodyHtmlContainsFooter(html: string): boolean {
  if (!html) return false;
  return /<section\b[^>]*\bdata-section-name\s*=\s*["']footer["']/i.test(html);
}

function escapeCssValue(value: string): string {
  return value.replace(/[<>"]/g, '').trim();
}

// Client-side mirror of api/src/modules/emails/util/stamp-image-ids.ts.
// The server stamps on persist and on read, but the agent's SSE stream and
// any uncommitted working copy bypass both — without a client-side pass the
// chat iframe shows unstamped <img> tags right after generation and the
// per-image toolbar can't fire (its selector is img[data-nm-img]). IDs are
// deterministic top-down so they match what the server writes to sourceHtml.
const IMG_TAG_REGEX = /<img\b([^>]*)>/gi;
const DATA_NM_IMG_ATTR_REGEX = /\s*data-nm-img\s*=\s*("[^"]*"|'[^']*'|\S+)/i;

export function stampImageIds(html: string): string {
  if (!html) return html;
  let index = 0;
  return html.replace(IMG_TAG_REGEX, (_match, attrs: string) => {
    const cleaned = attrs.replace(DATA_NM_IMG_ATTR_REGEX, '');
    const id = `img-${index++}`;
    const leading = cleaned.startsWith(' ') || cleaned === '' ? '' : ' ';
    return `<img${leading}${cleaned} data-nm-img="${id}">`;
  });
}

// Resolve `iconify:<prefix>:<name>?color=&size=` authoring URIs to the public
// Iconify SVG CDN, for the live/in-progress preview ONLY. While a turn is
// streaming, the chat renders the agent's raw authored markup before the server
// rewrites icons to hosted PNGs (resolveIconifyHtmlSrcs runs at persist) — so
// an `<img src="iconify:...">` would flash as a broken image. Rewriting it to
// `https://api.iconify.design/<prefix>/<name>.svg?color=%23<hex>&height=<px>`
// makes the real glyph show immediately (browsers render SVG-in-<img> fine;
// the Gmail/Outlook SVG-drop concern only applies to the delivered email, not
// this in-browser preview). A no-op on committed HTML, which already carries
// hosted PNG srcs. Mirrors resolveIconUrl in api/src/shared/email-renderer/icon-asset.ts.
const ICONIFY_PREVIEW_SRC_REGEX = /(\bsrc\s*=\s*)(["'])iconify:([^"']+)\2/gi;

export function resolveIconifyForPreview(html: string): string {
  if (!html || !html.includes('iconify:')) return html;
  return html.replace(ICONIFY_PREVIEW_SRC_REGEX, (full, pre: string, quote: string, rest: string) => {
    // rest = "<prefix>:<name>[?color=<hex>&size=<px>]" — entity-decode the query
    // separator so a &amp;-escaped attribute still parses.
    const decoded = rest.replace(/&amp;/gi, '&');
    const qIdx = decoded.indexOf('?');
    const id = qIdx === -1 ? decoded : decoded.slice(0, qIdx);
    const colonIdx = id.indexOf(':');
    if (colonIdx <= 0 || colonIdx >= id.length - 1) return full; // not prefix:name
    const prefix = id.slice(0, colonIdx);
    const name = id.slice(colonIdx + 1);
    const params = new URLSearchParams(qIdx === -1 ? '' : decoded.slice(qIdx + 1));
    const out = new URLSearchParams();
    const color = params.get('color');
    if (color) out.set('color', `#${color.replace(/^#/, '')}`);
    const size = params.get('size');
    if (size && /^\d+$/.test(size)) out.set('height', size);
    const query = out.toString(); // URLSearchParams encodes '#' -> %23
    return `${pre}${quote}https://api.iconify.design/${prefix}/${name}.svg${query ? `?${query}` : ''}${quote}`;
  });
}

// Remove the canvas round-trip payloads for display only — callers keep the
// originals in storage for lossless re-import. Two forms:
//   - legacy `<script type="application/json" data-nm-scene>...</script>`
//   - the `data-nm-doc="..."` attribute (a multi-KB embedded scene) that bloats
//     the rendered DOM and can throw off the auto-sizing iframe's height.
function stripSceneSidecars(html: string): string {
  return html
    .replace(/<script\b[^>]*\bdata-nm-scene\b[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/\sdata-nm-doc="[^"]*"/gi, '');
}

export function composeChatIframeHtml({
  headerHtml,
  footerHtml,
  bodyHtml,
  bodyBackgroundColor,
  bodyFontFamily,
}: {
  headerHtml: string | null;
  footerHtml: string | null;
  bodyHtml: string;
  bodyBackgroundColor?: string | null;
  bodyFontFamily?: string | null;
}): string {
  const headMatch = bodyHtml.match(/<head\b[^>]*>([\s\S]*?)<\/head>/i);
  const headInner = headMatch ? headMatch[1] : '';
  // Strip the canvas round-trip sidecar (`<script data-nm-scene>`) for display
  // only — the sandboxed iframe (no allow-scripts) logs a "blocked script"
  // warning for any <script>, and the JSON is dead weight in the preview. The
  // stored sourceHtml keeps it so canvas re-import stays lossless.
  const bodyInner = stripSceneSidecars(extractInnerBody(bodyHtml));
  const bg = bodyBackgroundColor ? escapeCssValue(bodyBackgroundColor) : '#ffffff';
  const font = bodyFontFamily ? escapeCssValue(bodyFontFamily) : null;
  const fontDecl = font ? `font-family:${font};` : '';
  return [
    '<!doctype html>',
    '<html><head><meta charset="utf-8" />',
    // Full-bleed shape dividers (waves/curves) are absolutely-positioned SVGs
    // that hang off a section edge (e.g. bottom:-40px) so their solid edge
    // visually continues the adjacent same-colored section. In the live DOM a
    // sub-pixel rounding gap between the section's background box and the SVG's
    // solid edge reveals the lighter neighbor as a faint 1px hairline. (The
    // exported email is rasterized by the backend slicer, so it never shows the
    // seam.) A tiny vertical scale bleeds the SVG's solid edge ~1px into its
    // adjacent section, closing the gap. scaleY extends BOTH edges, so it works
    // for top- and bottom-anchored dividers alike; the wavy edge bleeding a sub-
    // pixel over the contrasting neighbor is imperceptible. preserveAspectRatio
    // ="none" is the signature of these full-bleed dividers (icons use the
    // default xMidYMid), so it scopes the rule to dividers only.
    `<style>html,body{margin:0;padding:0;background:${bg};${fontDecl}overflow-x:hidden;scrollbar-width:none;}html::-webkit-scrollbar,body::-webkit-scrollbar{display:none;}body{width:100%;}a{text-decoration:none;color:inherit;}svg[preserveAspectRatio="none"]{transform:scaleY(1.05);transform-origin:center;transform-box:border-box;}</style>`,
    headInner,
    '</head><body>',
    headerHtml ?? '',
    bodyInner,
    footerHtml ?? '',
    '</body></html>',
  ].join('');
}
